import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { TileEdit } from './blocks';
import type { BeastEvent, BeastsMsg } from './beasts';

// ─────────────────────────────────────────────────────────────────────────────
// The live ocean: classmates' fish and building, as it happens. Supabase
// Realtime broadcast on private channels, ocean:<class>:<region>, that only
// the class can use (supabase/migrations/20261015_ocean_live.sql).
//
// Built to be frugal with Realtime messages (every message delivered to every
// listener counts against the project's quota):
//   • a student only hears their own region of the map (about 2400 wide)
//   • a fish sends at most 2 positions a second while moving, and a "still
//     here" every 4 seconds when not; others glide between updates
//   • the look is sent only now and then, not with every position
//   • sea beasts: one classmate per region runs them and shares them twice a
//     second, only while there are beasts or someone is down in their waters
// ─────────────────────────────────────────────────────────────────────────────

export const REGION = 2400;
export const regionOf = (x: number) => Math.max(0, Math.floor(x / REGION));

export type Pose = { x: number; y: number; vx: number; vy: number; f: 1 | -1; e: number };
export type PeerMsg = { id: string; p: Pose; look?: unknown };

type Handlers = {
  onPeer: (m: PeerMsg) => void;
  onLeave?: (id: string) => void;
  onTile?: (e: TileEdit) => void;
  /** Sea beasts of a region, from whoever is running them there. */
  onBeasts?: (m: BeastsMsg) => void;
  onBeastEvent?: (e: BeastEvent) => void;
  /** A classmate's emote. */
  onEmote?: (id: string, e: string) => void;
};

export class OceanLive {
  private cid: string;
  private me: string | null;
  private regions: number;
  private h: Handlers;
  private channels = new Map<number, RealtimeChannel>();
  private region = -1;
  private sent = 0;
  private lastSend = 0;
  private lastPose: Pose | null = null;
  private closed = false;

  /** `me` null = listen only (the teacher's view, which hears every region). */
  constructor(classroomId: string, me: string | null, mapWidth: number, h: Handlers) {
    this.cid = classroomId;
    this.me = me;
    this.regions = Math.max(1, Math.ceil(mapWidth / REGION));
    this.h = h;
    if (!me) for (let r = 0; r < this.regions; r++) this.join(r);
  }

  private join(r: number) {
    if (this.channels.has(r) || this.closed) return;
    const ch = supabase.channel(`ocean:${this.cid}:${r}`, { config: { private: true, broadcast: { self: false } } });
    ch.on('broadcast', { event: 'pos' }, ({ payload }) => { if (payload?.id !== this.me) this.h.onPeer(payload as PeerMsg); })
      .on('broadcast', { event: 'bye' }, ({ payload }) => { if (payload?.id) this.h.onLeave?.(payload.id); })
      .on('broadcast', { event: 'tile' }, ({ payload }) => { if (payload) this.h.onTile?.(payload as TileEdit); })
      .on('broadcast', { event: 'beasts' }, ({ payload }) => { if (payload) this.h.onBeasts?.(payload as BeastsMsg); })
      .on('broadcast', { event: 'bev' }, ({ payload }) => { if (payload) this.h.onBeastEvent?.(payload as BeastEvent); })
      .on('broadcast', { event: 'emote' }, ({ payload }) => { if (payload?.id && typeof payload.e === 'string') this.h.onEmote?.(payload.id, payload.e.slice(0, 8)); })
      .subscribe();
    this.channels.set(r, ch);
  }

  private drop(r: number) {
    const ch = this.channels.get(r);
    if (!ch) return;
    if (this.me) void ch.send({ type: 'broadcast', event: 'bye', payload: { id: this.me } });
    void supabase.removeChannel(ch);
    this.channels.delete(r);
  }

  /**
   * Call often (every frame is fine): sends only when it should. Switches region
   * when the fish swims into another one (with a little slack at the border).
   */
  tick(now: number, pose: Pose, extra: () => { look: unknown }) {
    if (!this.me || this.closed) return;
    const r = regionOf(pose.x);
    if (r !== this.region) {
      const mid = (this.region + 0.5) * REGION;
      if (this.region < 0 || Math.abs(pose.x - mid) > REGION / 2 + 150) {
        if (this.region >= 0) this.drop(this.region);
        this.region = r;
        this.join(r);
        this.sent = 0;            // a new region hears our look straight away
        this.lastSend = 0;
      }
    }
    const moving = Math.hypot(pose.vx, pose.vy) > 12;
    const moved = !this.lastPose || Math.hypot(pose.x - this.lastPose.x, pose.y - this.lastPose.y) > 6;
    const gap = now - this.lastSend;
    if (!((moving || moved) ? gap >= 500 : gap >= 4000)) return;
    const ch = this.channels.get(this.region);
    if (!ch) return;
    const msg: PeerMsg = { id: this.me, p: round(pose) };
    if (this.sent % 8 === 0) Object.assign(msg, extra());
    void ch.send({ type: 'broadcast', event: 'pos', payload: msg });
    this.sent++;
    this.lastSend = now;
    this.lastPose = pose;
  }

  /** Tell classmates in this region about a block change (it's already saved). */
  sendTile(e: TileEdit) {
    const ch = this.channels.get(this.region);
    if (ch && this.me) void ch.send({ type: 'broadcast', event: 'tile', payload: { tx: e.tx, ty: e.ty, tile: e.tile } });
  }

  /** Share this region's sea beasts (only the classmate running them does). */
  sendBeasts(m: BeastsMsg) {
    const ch = this.channels.get(m.r);
    if (ch && this.me) void ch.send({ type: 'broadcast', event: 'beasts', payload: m });
  }

  /** Tell whoever runs this region's beasts that one bit you or you hit it. */
  sendBeastEvent(e: BeastEvent) {
    const ch = this.channels.get(this.region);
    if (ch && this.me) void ch.send({ type: 'broadcast', event: 'bev', payload: e });
  }

  /** Show classmates in this region an emote over my fish. */
  sendEmote(e: string) {
    const ch = this.channels.get(this.region);
    if (ch && this.me) void ch.send({ type: 'broadcast', event: 'emote', payload: { id: this.me, e } });
  }

  leave() {
    this.closed = true;
    for (const r of [...this.channels.keys()]) this.drop(r);
  }
}

const round = (p: Pose): Pose => ({ x: Math.round(p.x), y: Math.round(p.y), vx: Math.round(p.vx), vy: Math.round(p.vy), f: p.f, e: Math.round(p.e * 10) / 10 });
